// Drag/resize support helpers shared between TimelineTrack and TimelineTransformer.
//
// All times are seconds. Indices reference the original (unsorted)
// `eventGroupState[primary].events` array.

import { snapTime, frameGapToSeconds } from "./snapping.js";

/**
 * Build the set of snap-target times for the current primary track. Excludes
 * the times belonging to events that are themselves being dragged so a
 * dragged region cannot snap to its own previous edge.
 *
 * @param {object} input
 * @param {Array<{start:number,end:number,id:string}>} input.events
 * @param {Set<number>} input.movingIndexes - indexes whose edges to ignore
 * @returns {number[]}
 */
export function gatherRegionEdgeTimes({ events, movingIndexes }) {
    const out = [];
    for (let i = 0; i < events.length; i += 1) {
        if (movingIndexes.has(i)) continue;
        const ev = events[i];
        if (!ev) continue;
        if (Number.isFinite(ev.start)) out.push(ev.start);
        if (Number.isFinite(ev.end)) out.push(ev.end);
    }
    return out;
}

/**
 * Apply snapping to a candidate time using the timeline's current
 * settings. Returns the (possibly snapped) time plus a snapped descriptor.
 *
 * @param {object} input
 * @param {number} input.time
 * @param {number} input.pxPerSec
 * @param {object} input.editorState
 * @param {object} input.markerState
 * @param {number[]} input.edgeTimes
 * @returns {{ time: number, snapped: false|object }}
 */
export function snapWithSettings({ time, pxPerSec, editorState, markerState, edgeTimes }) {
    if (!editorState) return { time, snapped: false };
    const snapMarkers = Boolean(editorState.snapToShotChanges);
    const snapEdges = Boolean(editorState.snapToRegions);
    if (!snapMarkers && !snapEdges) return { time, snapped: false };

    const markers = (() => {
        if (!snapMarkers || !markerState) return [];
        const list = markerState.lists?.[markerState.selected];
        if (!list || !Array.isArray(list.markers)) return [];
        return list.markers.map((m) => Number(m.time)).filter((t) => Number.isFinite(t));
    })();

    return snapTime({
        time,
        pxPerSec,
        markers,
        edges: snapEdges ? edgeTimes : [],
        snapMarkers,
        snapEdges
    });
}

/**
 * Clamp a proposed new `[start, end]` for a single region against its
 * immediate non-moving neighbors (by time), respecting a frame gap. Returns
 * the largest delta to start (in seconds) that can be applied while keeping
 * the region inside `[prev.end + gap, next.start - gap]`.
 *
 * @param {object} input
 * @param {{start:number, end:number}} input.original   - original region
 * @param {number} input.deltaStart                     - desired delta (sec)
 * @param {number} input.deltaEnd                       - desired delta (sec)
 * @param {{start:number,end:number}|null} input.prev
 * @param {{start:number,end:number}|null} input.next
 * @param {number} [input.frameGapSec]
 * @returns {{ deltaStart: number, deltaEnd: number }}
 */
export function clampMoveDelta({ original, deltaStart, deltaEnd, prev, next, frameGapSec = 0 }) {
    let ds = deltaStart;
    let de = deltaEnd;
    if (prev && Number.isFinite(prev.end)) {
        const minStart = prev.end + frameGapSec;
        const allowed = original.start + ds - minStart;
        if (allowed < 0) {
            ds -= allowed;
            de -= allowed; // pure-move case (ds == de) keeps both in lockstep
        }
    }
    if (next && Number.isFinite(next.start)) {
        const maxEnd = next.start - frameGapSec;
        const allowed = maxEnd - (original.end + de);
        if (allowed < 0) {
            ds += allowed;
            de += allowed;
        }
    }
    return { deltaStart: ds, deltaEnd: de };
}

/**
 * Compute the hard min/max time bounds for a single resize handle. Considers
 * ALL other events (so overlapping/unsorted events are handled), the timeline
 * start (0), the media duration, the region's own opposite edge (min
 * duration), and a frame gap. The returned range is the authoritative limit a
 * handle may move within; snapping must never push past it.
 *
 * @param {object} input
 * @param {Array<{start:number, end:number}>} input.events
 * @param {number} input.eventIndex            - index of the region being resized
 * @param {"left"|"right"} input.side
 * @param {number} [input.frameGapSec]
 * @param {number} [input.minDurationSec]
 * @param {number} [input.duration]            - media duration (upper bound)
 * @returns {{ min: number, max: number }}
 */
export function computeHandleBounds({
    events,
    eventIndex,
    side,
    frameGapSec = 0,
    minDurationSec = 0.05,
    duration = Infinity
}) {
    const me = events?.[eventIndex];
    if (!me) return { min: 0, max: duration };

    if (side === "left") {
        let lower = 0;
        for (let i = 0; i < events.length; i += 1) {
            if (i === eventIndex) continue;
            const ev = events[i];
            if (!ev || ev.end === false || !Number.isFinite(ev.end)) continue;
            // Events that end at or before this region's start sit to the left;
            // the closest such edge (plus gap) is the lower bound.
            if (ev.end <= me.start && ev.end + frameGapSec > lower) {
                lower = ev.end + frameGapSec;
            }
        }
        // Guard against inverted bounds for degenerate (sub-min-duration) regions.
        return { min: lower, max: Math.max(lower, me.end - minDurationSec) };
    }

    // side === "right"
    let upper = duration;
    for (let i = 0; i < events.length; i += 1) {
        if (i === eventIndex) continue;
        const ev = events[i];
        if (!ev || ev.start === false || !Number.isFinite(ev.start)) continue;
        // Events that start at or after this region's end sit to the right; the
        // closest such edge (minus gap) is the upper bound.
        if (ev.start >= me.end && ev.start - frameGapSec < upper) {
            upper = ev.start - frameGapSec;
        }
    }
    // The media duration must never force the end below where it already sits
    // (e.g. imported events that extend past a not-yet-known duration).
    if (upper < me.end) upper = me.end;
    const min = me.start + minDurationSec;
    return { min, max: Math.max(min, upper) };
}

/**
 * Find the closest snap target to `time`. Markers and region edges both use
 * PIXEL-based tolerances (converted via `pxPerSec`) so snap feel stays
 * consistent across zoom levels. The single nearest qualifying target across
 * both groups wins. An optional `inBounds` predicate discards targets the
 * handle can't legally reach.
 *
 * @param {object} input
 * @param {number} input.time
 * @param {number[]} [input.markers]
 * @param {number[]} [input.edges]
 * @param {number} input.pxPerSec
 * @param {number} [input.edgeTolerancePx]
 * @param {number} [input.markerTolerancePx]
 * @param {((t:number)=>boolean)|null} [input.inBounds]
 * @returns {{ distance: number, target: number, type: 'marker'|'edge' } | null}
 */
export function nearestSnapTarget({
    time,
    markers = [],
    edges = [],
    pxPerSec,
    edgeTolerancePx = 6,
    markerTolerancePx = 6,
    inBounds = null
}) {
    if (!Number.isFinite(time) || !pxPerSec) return null;
    const markerTol = markerTolerancePx / pxPerSec;
    const edgeTol = edgeTolerancePx / pxPerSec;
    let best = null;
    const consider = (t, tol, type) => {
        if (!Number.isFinite(t)) return;
        if (inBounds && !inBounds(t)) return;
        const d = Math.abs(t - time);
        if (d <= tol && (!best || d < best.distance)) best = { distance: d, target: t, type };
    };
    for (let i = 0; i < markers.length; i += 1) consider(markers[i], markerTol, "marker");
    for (let i = 0; i < edges.length; i += 1) consider(edges[i], edgeTol, "edge");
    return best;
}

/**
 * Snap a desired handle time to the nearest in-range snap target, then
 * hard-clamp to the supplied bounds. Targets outside `[bounds.min, bounds.max]`
 * are discarded BEFORE snapping so a snap target can never drag the handle past
 * a neighbor boundary.
 *
 * @param {object} input
 * @param {number} input.desiredTime
 * @param {{min:number, max:number}} input.bounds
 * @param {number[]} [input.markers]
 * @param {number[]} [input.edges]
 * @param {number} input.pxPerSec
 * @param {number} [input.edgeTolerancePx]
 * @param {number} [input.markerTolerancePx]
 * @returns {number}
 */
export function resolveHandleTime({
    desiredTime,
    bounds,
    markers = [],
    edges = [],
    pxPerSec,
    edgeTolerancePx = 6,
    markerTolerancePx = 6
}) {
    const inBounds = (t) => t >= bounds.min && t <= bounds.max;
    const best = nearestSnapTarget({
        time: desiredTime,
        markers,
        edges,
        pxPerSec,
        edgeTolerancePx,
        markerTolerancePx,
        inBounds
    });
    const time = best ? best.target : desiredTime;
    return Math.min(Math.max(time, bounds.min), bounds.max);
}

/**
 * Find the time-adjacent prev/next neighbors of an event by index, excluding
 * the supplied moving indexes.
 *
 * @param {object[]} events
 * @param {number} eventIndex
 * @param {Set<number>} movingIndexes
 * @returns {{ prev: object|null, next: object|null }}
 */
export function findNeighbors(events, eventIndex, movingIndexes) {
    const me = events[eventIndex];
    if (!me) return { prev: null, next: null };
    let prev = null;
    let next = null;
    for (let i = 0; i < events.length; i += 1) {
        if (i === eventIndex || movingIndexes.has(i)) continue;
        const ev = events[i];
        if (!ev || ev.start === false || ev.end === false) continue;
        if (ev.start < me.start) {
            if (!prev || ev.start > prev.start) prev = ev;
        } else if (ev.start >= me.start) {
            if (!next || ev.start < next.start) next = ev;
        }
    }
    return { prev, next };
}

/**
 * Compute the largest delta we can apply to ALL events in `movingIndexes`
 * such that no event collides with its non-moving neighbors. Used so that a
 * pure-move drag of multi-selected regions doesn't crush some of them into
 * neighbors before others have a chance to clamp.
 *
 * @param {object} input
 * @param {object[]} input.events
 * @param {Set<number>} input.movingIndexes
 * @param {number} input.delta   - desired uniform delta in seconds
 * @param {number} [input.frameGapSec]
 * @returns {number}
 */
export function clampUniformDelta({ events, movingIndexes, delta, frameGapSec = 0 }) {
    let result = delta;
    for (const idx of movingIndexes) {
        const me = events[idx];
        if (!me) continue;
        const { prev, next } = findNeighbors(events, idx, movingIndexes);
        if (prev && Number.isFinite(prev.end)) {
            const minStart = prev.end + frameGapSec;
            const allowed = me.start + result - minStart;
            if (allowed < 0) result -= allowed;
        }
        if (next && Number.isFinite(next.start)) {
            const maxEnd = next.start - frameGapSec;
            const allowed = maxEnd - (me.end + result);
            if (allowed < 0) result += allowed;
        }
    }
    return result;
}

const FILMSTRIP_MIN_DURATION_SEC = 0.05;

/**
 * Set an event's start time from a filmstrip double-click and trim the
 * time-adjacent previous event's end when it intrudes into the new start
 * (respecting `frameGapSec`). Does not adjust the selected event's end for
 * overlap with the next event.
 *
 * @param {object} input
 * @param {object[]} input.events
 * @param {number} input.eventIndex
 * @param {number} input.newStart - start time in seconds
 * @param {number} [input.frameGapSec]
 * @param {number} [input.minDurationSec]
 * @returns {boolean}
 */
export function applyFilmstripStartTime({
    events,
    eventIndex,
    newStart,
    frameGapSec = 0,
    minDurationSec = FILMSTRIP_MIN_DURATION_SEC
}) {
    const ev = events?.[eventIndex];
    if (!ev || !Number.isFinite(newStart)) return false;

    const originalDuration =
        ev.end === false || ev.start === false || !Number.isFinite(ev.end) || !Number.isFinite(ev.start)
            ? 2
            : ev.end - ev.start;

    const clampedStart = Math.max(0, newStart);
    ev.start = clampedStart;

    if (!Number.isFinite(ev.end) || ev.end < ev.start) {
        ev.end = ev.start + (originalDuration > 0 ? originalDuration : minDurationSec);
    }

    const { prev } = findNeighbors(events, eventIndex, new Set());
    if (prev && Number.isFinite(prev.end) && prev.end + frameGapSec > clampedStart) {
        prev.end = clampedStart - frameGapSec;
        if (Number.isFinite(prev.start) && prev.end < prev.start + minDurationSec) {
            prev.end = prev.start + minDurationSec;
        }
    }

    return true;
}

/**
 * Find the index of the event to ripple when overlap is allowed. Uses edge
 * proximity (not start-order) so it behaves correctly even when events already
 * overlap or the array is unsorted: for "left" it returns the event with the
 * largest end that still ends at/before the reference start; for "right" the
 * event with the smallest start that still starts at/after the reference end.
 *
 * @param {object[]} events
 * @param {number} eventIndex          - reference event (edge being dragged)
 * @param {"left"|"right"} side
 * @param {Set<number>|null} [excludeIndexes] - indexes to skip (e.g. all moving events)
 * @returns {number} index of the ripple target, or -1 if none
 */
export function findRippleTargetIndex(events, eventIndex, side, excludeIndexes = null) {
    const me = events?.[eventIndex];
    if (!me) return -1;
    const skip = (i) => i === eventIndex || (excludeIndexes && excludeIndexes.has(i));
    let target = -1;
    if (side === "left") {
        let best = -Infinity;
        for (let i = 0; i < events.length; i += 1) {
            if (skip(i)) continue;
            const ev = events[i];
            if (!ev || !Number.isFinite(ev.end)) continue;
            if (ev.end <= me.start && ev.end > best) {
                best = ev.end;
                target = i;
            }
        }
    } else {
        let best = Infinity;
        for (let i = 0; i < events.length; i += 1) {
            if (skip(i)) continue;
            const ev = events[i];
            if (!ev || !Number.isFinite(ev.start)) continue;
            if (ev.start >= me.end && ev.start < best) {
                best = ev.start;
                target = i;
            }
        }
    }
    return target;
}

/**
 * Compute resize-handle bounds when overlap is ALLOWED. Unlike
 * `computeHandleBounds` it ignores neighbor edges entirely; the only limits are
 * the timeline start (0), the media duration, and the region's own opposite
 * edge (min duration). Overlap correction is handled separately by rippling the
 * neighbor.
 *
 * @param {object} input
 * @param {Array<{start:number, end:number}>} input.events
 * @param {number} input.eventIndex
 * @param {"left"|"right"} input.side
 * @param {number} [input.minDurationSec]
 * @param {number} [input.duration]
 * @returns {{ min: number, max: number }}
 */
export function computePermissiveHandleBounds({
    events,
    eventIndex,
    side,
    minDurationSec = 0.05,
    duration = Infinity
}) {
    const me = events?.[eventIndex];
    if (!me) return { min: 0, max: duration };
    if (side === "left") {
        return { min: 0, max: Math.max(0, me.end - minDurationSec) };
    }
    const upper = Math.max(duration, me.end);
    const min = me.start + minDurationSec;
    return { min, max: Math.max(min, upper) };
}

/**
 * Apply a resize when overlap is allowed: move the dragged edge to `newTime`
 * (clamped to permissive bounds) and, if that edge now intrudes on the cached
 * ripple neighbor, push that neighbor's touching edge to preserve `frameGapSec`.
 * Neighbors are never shrunk below `minDurationSec`.
 *
 * @param {object} input
 * @param {object[]} input.events
 * @param {number} input.eventIndex
 * @param {"left"|"right"} input.side
 * @param {number} input.newTime
 * @param {number} [input.leftRippleIndex]
 * @param {number} [input.rightRippleIndex]
 * @param {number} [input.frameGapSec]
 * @param {number} [input.minDurationSec]
 * @param {number} [input.duration]
 * @returns {void}
 */
export function applyResizeRipple({
    events,
    eventIndex,
    side,
    newTime,
    leftRippleIndex = -1,
    rightRippleIndex = -1,
    frameGapSec = 0,
    minDurationSec = 0.05,
    duration = Infinity
}) {
    const me = events?.[eventIndex];
    if (!me || !Number.isFinite(newTime)) return;
    const bounds = computePermissiveHandleBounds({ events, eventIndex, side, minDurationSec, duration });
    const clamped = Math.min(Math.max(newTime, bounds.min), bounds.max);

    if (side === "left") {
        me.start = clamped;
        const prev = leftRippleIndex >= 0 ? events[leftRippleIndex] : null;
        if (prev && Number.isFinite(prev.end) && prev.end + frameGapSec > clamped) {
            prev.end = clamped - frameGapSec;
            if (Number.isFinite(prev.start) && prev.end < prev.start + minDurationSec) {
                prev.end = prev.start + minDurationSec;
            }
        }
    } else {
        me.end = clamped;
        const next = rightRippleIndex >= 0 ? events[rightRippleIndex] : null;
        if (next && Number.isFinite(next.start) && next.start - frameGapSec < clamped) {
            next.start = clamped + frameGapSec;
            if (Number.isFinite(next.end) && next.start > next.end - minDurationSec) {
                next.start = next.end - minDurationSec;
            }
        }
    }
}

/**
 * Apply a whole-region move when overlap is allowed: shift every moving event by
 * `delta`, then ripple the cached left/right neighbors against the moving
 * cluster's outermost edges to preserve `frameGapSec`. Only one side actually
 * ripples for a given drag direction (moving left touches the previous event,
 * moving right touches the next), so caching both is safe.
 *
 * @param {object} input
 * @param {object[]} input.events
 * @param {Set<number>} input.movingIndexes
 * @param {number} input.delta
 * @param {number} [input.leftRippleIndex]
 * @param {number} [input.rightRippleIndex]
 * @param {number} [input.frameGapSec]
 * @param {number} [input.minDurationSec]
 * @returns {void}
 */
export function applyMoveRipple({
    events,
    movingIndexes,
    delta,
    leftRippleIndex = -1,
    rightRippleIndex = -1,
    frameGapSec = 0,
    minDurationSec = 0.05
}) {
    if (!Number.isFinite(delta)) return;
    let minStart = Infinity;
    let maxEnd = -Infinity;
    for (const idx of movingIndexes) {
        const ev = events[idx];
        if (!ev) continue;
        ev.start += delta;
        ev.end += delta;
        if (Number.isFinite(ev.start) && ev.start < minStart) minStart = ev.start;
        if (Number.isFinite(ev.end) && ev.end > maxEnd) maxEnd = ev.end;
    }

    const prev = leftRippleIndex >= 0 ? events[leftRippleIndex] : null;
    if (prev && Number.isFinite(prev.end) && Number.isFinite(minStart) && prev.end + frameGapSec > minStart) {
        prev.end = minStart - frameGapSec;
        if (Number.isFinite(prev.start) && prev.end < prev.start + minDurationSec) {
            prev.end = prev.start + minDurationSec;
        }
    }

    const next = rightRippleIndex >= 0 ? events[rightRippleIndex] : null;
    if (next && Number.isFinite(next.start) && Number.isFinite(maxEnd) && next.start - frameGapSec < maxEnd) {
        next.start = maxEnd + frameGapSec;
        if (Number.isFinite(next.end) && next.start > next.end - minDurationSec) {
            next.start = next.end - minDurationSec;
        }
    }
}
